import styled from '@emotion/styled'
import React, { useRef, useState, useEffect } from 'react'
import { FaCircleChevronLeft, FaCircleChevronRight } from 'react-icons/fa6'
import { hideScrollbar } from 'shop/components/common'
import { Prompt as PromptType } from 'shop/components/Landing/types'
import { StyledHTMLElement } from 'shop/theme/types'
import ShopPrompt from './ShopPrompt'

type Props = {
  prompts: PromptType[] | undefined
}

type Position = 'left' | 'right'

const ShopPrompts = ({ prompts }: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const showScrollButtons = (prompts?.length || 0) > 2

  useEffect(() => {
    updateScrollButtons()
    window.addEventListener('resize', updateScrollButtons)
    return () => window.removeEventListener('resize', updateScrollButtons)
  }, [prompts])

  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 1)
      // Add 1px tolerance to account for subpixel rounding when smooth scrolling
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
    }
  }

  /** Handles scroll behaviour on left and right button click */
  const scroll = (position: Position) => {
    if (scrollContainerRef.current && scrollContainerRef.current.children[0]) {
      const container = scrollContainerRef.current
      const firstPrompt = container.children[0] as HTMLElement

      // Calculate scroll distance based on prompt width + gap between prompts
      const promptWidth = firstPrompt.offsetWidth
      const gap = parseInt(getComputedStyle(container).gap)
      const containerPadding = parseInt(getComputedStyle(container).paddingLeft)

      // Get current scroll position & calculate target with padding offset
      const currentScrollLeft = container.scrollLeft
      const scrollDistance = promptWidth + gap
      const targetScrollLeft =
        position === 'left'
          ? currentScrollLeft - scrollDistance + containerPadding
          : currentScrollLeft + scrollDistance - containerPadding

      container.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      })
    }
  }

  const createScrollButton = (position: Position) => {
    const shouldShowScrollButton =
      position === 'left' ? canScrollLeft : canScrollRight
    const Icon =
      position === 'left' ? FaCircleChevronLeft : FaCircleChevronRight

    return (
      <ScrollButton
        data-testid={`scroll-button-${position}`}
        position={position}
        visible={shouldShowScrollButton}
        onClick={() => scroll(position)}
      >
        <Icon />
      </ScrollButton>
    )
  }

  if (!prompts?.length) return null

  return (
    <Wrapper>
      {showScrollButtons && createScrollButton('left')}
      <Container
        ref={scrollContainerRef}
        data-testid='shop-prompts'
        onScroll={updateScrollButtons}
      >
        {prompts.map((prompt, index) => (
          <React.Fragment key={prompt.description || index}>
            <ShopPrompt
              prompt={prompt}
              hasMultiplePrompts={showScrollButtons}
            />
          </React.Fragment>
        ))}
      </Container>
      {showScrollButtons && createScrollButton('right')}
    </Wrapper>
  )
}

const Wrapper = styled.div({
  position: 'relative',
  width: '100%',
  padding: '16px 0'
})

const Container = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  width: '100%',
  overflowX: 'auto',
  // Forces scroll to snap to prompts on horizontal axis
  scrollSnapType: 'x mandatory',
  padding: '0 16px',
  [theme.mediaQueries.viewport7]: {
    padding: '0'
  },
  ...(hideScrollbar as any)
}))

const ScrollButton = styled.div<{ position: Position; visible: boolean }>(
  ({ position, visible, theme }) => {
    const mobilePositions = {
      left: { left: '5px' },
      right: { right: '5px' }
    }
    const desktopPositions = {
      left: { left: '-15px' },
      right: { right: '-15px' }
    }

    return {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      ...mobilePositions[position],
      width: '32px',
      height: '32px',
      cursor: visible ? 'pointer' : 'default',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1,
      opacity: visible ? 0.7 : 0,
      pointerEvents: visible ? 'auto' : 'none',
      transition: 'opacity 0.3s ease-in-out',
      '> svg': {
        width: '32px',
        height: '32px'
      },
      '&:hover': {
        opacity: visible ? 1 : 0
      },
      [theme.mediaQueries.viewport7]: {
        ...desktopPositions[position]
      }
    }
  }
)

export default ShopPrompts
